import { useCallback, useState } from 'react';
import MuiSelect, { SelectChangeEvent, SelectProps } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Arrow from '@/assets/icons/arrow-down-black.svg';
import { styled } from '@mui/material/styles';
import Alert from '../alert';
import './index.css';
export interface ISelectOption {
    label: string;
    value: string | number;
}

export type ISelectOptions = ISelectOption[];

interface IProps extends Omit<SelectProps, 'placeholder' | 'onChange'> {
    label?: string;
    placeholder?: string;
    options?: ISelectOptions;
    required?: boolean;
    wrapperClassName?: string;
    error?: boolean;
    errorMessage?: string;
    testId?: string;
    onChange?: (value: string) => void;
}

const Icon = styled('img')<{ open: boolean }>(({ open }) => ({
    width: '16px',
    height: '16px',
    transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
    transition: 'transform 0.3s ease'
}));

export const Select = ({
    wrapperClassName = '',
    placeholder,
    options,
    label,
    error,
    errorMessage,
    required = false,
    sx,
    onChange,
    testId = 'base-select',
    ...props
}: IProps) => {
    const [open, setOpen] = useState(false);

    const handleOnChange = useCallback(
        (e: SelectChangeEvent<unknown>) => {
            onChange?.(String(e.target.value));
        },
        [onChange]
    );

    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };
    const border = error ? '1px solid red' : `1px solid rgba(0, 0, 0, 0.6)`;
    return (
        <div className={`flex flex-col ${wrapperClassName ? wrapperClassName : 'min-w-56 max-w-full'}`} data-testid={`${testId}-container`}>
            {label && (
                <div data-testid={`${testId}-title`} className="mb-2 text-lg text-black lg:text-xl">
                    {required && <span className="font-bold text-red-500">* </span>}
                    <span className="font-atkinsonHyperlegible">{label}</span>
                </div>
            )}
            <MuiSelect
                displayEmpty
                error={error}
                onOpen={handleOpen}
                onClose={handleClose}
                data-testid={`${testId}-select`}
                IconComponent={() => (
                    <div
                        className="flex h-[100%] min-h-[30px] items-center justify-center"
                        onClick={() => {
                            setOpen(!open);
                        }}
                    >
                        <Icon alt="dropdown icon" open={open} src={Arrow} style={{ cursor: 'pointer' }} />
                    </div>
                )}
                open={open}
                inputProps={{ 'aria-label': 'Without label' }}
                MenuProps={{
                    PaperProps: {
                        sx: {
                            bgcolor: 'transparent',
                            paddingTop: '1px',
                            boxShadow: '0px 4px 4px 2px #00000005',
                            border: '0.5px solid #999999',
                            borderTopWidth: 0,
                            borderRadius: '0 0 8px 8px',
                            '& .MuiList-root': {
                                padding: 0,
                                bgcolor: 'white',
                                '& .MuiMenuItem-root ': {
                                    bgcolor: 'transparent',
                                    '&:hover': {
                                        bgcolor: 'primary.50'
                                    }
                                }
                            },
                            '& .MuiMenuItem-root': {
                                fontSize: '16px',
                                padding: '9px 20px',
                                backgroundColor: '#f4f4f4',
                                '&:hover': {
                                    backgroundColor: '#e0e0e0'
                                }
                            }
                        }
                    }
                }}
                onChange={handleOnChange}
                {...props}
                sx={{
                    bgcolor: 'white',
                    padding: '7px 17px',
                    maxWidth: '100%',
                    boxBhadow: '0px 1px 4px 0px #0000001A',
                    border,
                    // borderRadius: '8px',
                    zIndex: 20,
                    fieldset: {
                        border: 'none'
                    },
                    '& .MuiSelect-select': {
                        padding: 0,
                        fontFamily: 'DM Sans',
                        color: '#000'
                    },
                    ...sx
                }}
            >
                <MenuItem data-testid={`${testId}-placeholder`} value="" sx={{ display: 'none' }}>
                    <em className="font-dmSans text-[#999999]">{placeholder}</em>
                </MenuItem>
                {options?.map((option, idx) => (
                    <MenuItem data-testid={`${testId}-select-option-${idx}`} key={idx + option.value.toString()} value={option.value}>
                        {option.label}
                    </MenuItem>
                ))}
            </MuiSelect>

            {error && errorMessage && <Alert className="mt-2.5 font-dmSans lg:mt-6" label={errorMessage} />}
        </div>
    );
};

export default Select;
